import {
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonModal,
  IonRow,
} from "@ionic/react";
import React, { useMemo, useState } from "react";
import { BhpThemeHeaderWithBack } from "../pages/BussinessHomePage";
import CheckMarkItem from "../UI/CheckmarkItem";
import "./styles/BhpOnlineHealth.css";
import deepCopy from "../common/deepcopy";
import {
  arrowForward,
  checkmarkCircle,
  closeCircle,
  ellipse,
} from "ionicons/icons";
import ProgressSlider from "../UI/ProgressSlider";
import { keyExists } from "../common/commonFunctions";

interface BhpOnlineHealthDataType {
  website?: boolean;
  digitalBusinessCard?: boolean;
  googleBusinessListing?: boolean;
  sitesLinked?: boolean;
  marketPageLinked?: boolean;
  reviewSystem?: boolean;
  reviewsOnGoogle?: boolean;
  digitalMarketBanner?: boolean;
}

interface BhpCustomerExperienceType {
  customerReviewSystem: boolean;
  productUpdateSystem: boolean;
  customerDatabase: boolean;
  customerMessaging: boolean;
  marketAdvertisement: boolean;
  customerDiscountsOrRewards: boolean;
  customerReferralProgram: boolean;
  marketDataDecision: boolean;
}

/***************************************************************************************
 * Name: BhpOnlineHealth
 * Description: Component to pop up the evaluator for displaying the online health
 *              of the business
 ***************************************************************************************/
const BhpOnlineHealth: React.FC<{
  mode: "ONLINE_HEALTH" | "CUSTOMER_EX";
  open: boolean;
  onDismiss: () => void;
  marketName: string;
  clickNext: () => void;
  websiteStrength: number;
}> = (props) => {
  const onDismiss = () => {
    props.onDismiss();
  };
  const [onlineHealth, setOnlineHealth] = useState<BhpOnlineHealthDataType>({});
  const [customerEx, setCustomerEx] = useState<BhpCustomerExperienceType>({
    customerDatabase: false,
    customerDiscountsOrRewards: false,
    customerMessaging: false,
    customerReferralProgram: false,
    customerReviewSystem: false,
    marketAdvertisement: false,
    marketDataDecision: false,
    productUpdateSystem: false,
  });

  const customerExScore = useMemo(() => {
    let total = 0;
    let score = 0;
    //customerDatabase: false,
    total += 15;
    score += customerEx.customerDatabase ? 15 : 0;
    //customerDiscountsOrRewards: false,
    total += 10;
    score += customerEx.customerDiscountsOrRewards ? 10 : 0;
    //customerMessaging: false,
    total += 10;
    score += customerEx.customerMessaging ? 10 : 0;
    //customerReferralProgram: false,
    total += 10;
    score += customerEx.customerReferralProgram ? 10 : 0;
    //customerReviewSystem: false,
    total += 20;
    score += customerEx.customerReviewSystem ? 20 : 0;
    //marketAdvertisement: false,
    total += 10;
    score += customerEx.marketAdvertisement ? 10 : 0;
    //marketDataDecision: false,
    total += 10;
    score += customerEx.marketDataDecision ? 10 : 0;
    //productUpdateSystem: false,
    total += 15;
    score += customerEx.productUpdateSystem ? 15 : 0;
    return Math.floor((score / total) * 100);
  }, [customerEx]);

  const healthScore = useMemo(() => {
    let total = 0;
    let score = 0;
    const weight = {
      website: 10,
      digitalBusinessCard: 5,
      marketPageLinked: 10,
      reviewSystem: 10,
      googleBusinessListing: 15,
      sitesLinked: 20,
      reviewsOnGoogle: 10,
      digitalMarketBanner: 25,
    };
    for (const key of Object.keys(weight)) {
      if (keyExists(key, onlineHealth))
        score += onlineHealth[key] ? weight[key] : 0;
    }
    return score;
  }, [onlineHealth]);

  const updateOnlineHealth = (
    key:
      | "reviewSystem"
      | "digitalBusinessCard"
      | "website"
      | "marketPageLinked"
      | "googleBusinessListing"
      | "sitesLinked"
      | "reviewsOnGoogle"
      | "digitalMarketBanner",
    value: boolean
  ) => {
    setOnlineHealth((prevState) => {
      const retVal: BhpOnlineHealthDataType = deepCopy(prevState);
      retVal[key] = value;
      // if (subKey === "present" && !value) {
      //   retVal[key]["websiteLinked"] = false;
      // }
      return retVal;
    });
  };

  const updateCustomerEx = (
    key:
      | "customerReviewSystem"
      | "productUpdateSystem"
      | "customerDatabase"
      | "customerMessaging"
      | "marketAdvertisement"
      | "customerDiscountsOrRewards"
      | "customerReferralProgram"
      | "marketDataDecision",
    value: boolean
  ) => {
    setCustomerEx((prevState) => {
      const retVal: BhpCustomerExperienceType = deepCopy(prevState);
      retVal[key] = value;
      return retVal;
    });
  };

  const customerExChecklistArray: {
    key:
      | "customerReviewSystem"
      | "productUpdateSystem"
      | "customerDatabase"
      | "customerMessaging"
      | "marketAdvertisement"
      | "customerDiscountsOrRewards"
      | "customerReferralProgram"
      | "marketDataDecision";
    label: string;
  }[] = [
    {
      key: "customerReviewSystem",
      label: "Do you have a customer review system?",
    },
    {
      key: "productUpdateSystem",
      label: "Do you have a product update system?",
    },
    { key: "customerDatabase", label: "Do you have a customer database?" },
    {
      key: "customerMessaging",
      label: "Do you have a system to send messages to your customers?",
    },
    {
      key: "marketAdvertisement",
      label: `Do you have a system to run advertisements in ${props.marketName}`,
    },
    {
      key: "customerDiscountsOrRewards",
      label: `Do you have a customer discount or rewards program?`,
    },
    {
      key: "customerReferralProgram",
      label: "Do you have a referral program to attract new customer?",
    },
    {
      key: "marketDataDecision",
      label: "Do you have access to market data to help in business decisions?",
    },
  ];

  const onlineHealthChecklistArray: {
    key:
      | "reviewSystem"
      | "digitalBusinessCard"
      | "website"
      | "marketPageLinked"
      | "googleBusinessListing"
      | "sitesLinked"
      | "reviewsOnGoogle"
      | "digitalMarketBanner";
    label: string;
    subTitle?: string;
  }[] = [
    {
      key: "reviewSystem",
      label: "Review system",
    },
    {
      key: "digitalBusinessCard",
      label: "Digital business card",
    },
    { key: "website", label: "Personal website" },
    {
      key: "marketPageLinked",
      label: `Presence in ${props.marketName} online`,
    },

    {
      key: "sitesLinked",
      label: "Website linked to social sites",
      subTitle: "WhatsApp, Instagram, Facebook etc.",
    },
    {
      key: "googleBusinessListing",
      label: "Google business listing",
    },
    {
      key: "reviewsOnGoogle",
      label: "Reviews visible on Google",
    },
    {
      key: "digitalMarketBanner",
      label: "Digital market banner",
    },
  ];

  const getOnlineHealthChecklistRow = (
    idx: number,
    label: string,
    key:
      | "reviewSystem"
      | "digitalBusinessCard"
      | "website"
      | "marketPageLinked"
      | "googleBusinessListing"
      | "sitesLinked"
      | "reviewsOnGoogle"
      | "digitalMarketBanner",
    subTitle?: string
  ) => {
    const colCss =
      idx % 2
        ? "bhp-online-health-table-col-even"
        : "bhp-online-health-table-col-odd";
    const valPrimary = onlineHealth[key];
    const _keyExists = keyExists(key, onlineHealth);

    return (
      <IonRow className="bhp-online-health-table-row">
        <IonCol className={colCss} size="8">
          <div>{label}</div>
          <div style={{ fontSize: "0.7rem", color: "var(--ion-color-medium)" }}>
            {subTitle}
          </div>
        </IonCol>
        <IonCol
          className={colCss}
          size="2"
          onClick={() => {
            updateOnlineHealth(key, true);
          }}
        >
          <div
            className="bhp-online-health-center-align"
            style={{ fontSize: "1.2rem" }}
          >
            <IonIcon
              icon={checkmarkCircle}
              color={_keyExists && valPrimary ? "primary" : "medium"}
            />
          </div>
        </IonCol>
        <IonCol
          className={colCss}
          size="2"
          onClick={() => {
            updateOnlineHealth(key, false);
          }}
        >
          <div
            className="bhp-online-health-center-align"
            style={{ fontSize: "1.2rem" }}
          >
            <IonIcon
              icon={closeCircle}
              color={_keyExists && !valPrimary ? "secondary" : "medium"}
            />
          </div>
        </IonCol>
        {/* <IonCol
          className={colCss}
          size="2.5"
          onClick={() => {
            updateOnlineHealth(key, "websiteLinked", !valSecondary);
          }}
        >
          <div
            className="bhp-online-health-center-align"
            style={{ fontSize: "1.2rem" }}
          >
            <IonIcon
              icon={valSecondary ? checkmarkCircle : ellipse}
              color={valSecondary ? "primary" : "medium"}
            />
          </div>
        </IonCol> */}
      </IonRow>
    );
  };

  const getCustomerExChecklistRow = (
    idx: number,
    label: string,
    key:
      | "customerReviewSystem"
      | "productUpdateSystem"
      | "customerDatabase"
      | "customerMessaging"
      | "marketAdvertisement"
      | "customerDiscountsOrRewards"
      | "customerReferralProgram"
      | "marketDataDecision"
  ) => {
    const colCss =
      idx % 2
        ? "bhp-online-health-table-col-even"
        : "bhp-online-health-table-col-odd";
    const valPrimary = customerEx[key];

    return (
      <IonRow className="bhp-online-health-table-row">
        <IonCol className={colCss} size="9.5">
          {label}
        </IonCol>
        <IonCol
          className={colCss}
          size="2.5"
          onClick={() => {
            updateCustomerEx(key, !valPrimary);
          }}
        >
          <div
            className="bhp-online-health-center-align"
            style={{ fontSize: "1.2rem" }}
          >
            <IonIcon
              icon={valPrimary ? checkmarkCircle : ellipse}
              color={valPrimary ? "primary" : "medium"}
            />
          </div>
        </IonCol>
      </IonRow>
    );
  };

  const customerExJsx = (
    <IonGrid>
      <IonRow>
        <IonCol size="9.5" className="bhp-online-health-table-header-col">
          Item
        </IonCol>
        <IonCol size="2.5" className="bhp-online-health-table-header-col">
          <div style={{ textAlign: "center", width: "100%" }}>Present</div>
        </IonCol>
      </IonRow>
      {customerExChecklistArray.map((el, idx) => {
        return getCustomerExChecklistRow(idx, el.label, el.key);
      })}
    </IonGrid>
  );

  const onlineHealthJsx = (
    <IonGrid>
      <IonRow>
        <IonCol size="8" className="bhp-online-health-table-header-col">
          Item
        </IonCol>
        {/* <IonCol size="2.5" className="bhp-online-health-table-header-col">
          Present
        </IonCol> */}
        <IonCol size="4" className="bhp-online-health-table-header-col">
          {/* Website Linked */}
          Present
        </IonCol>
      </IonRow>
      {/* <IonRow className="bhp-online-health-table-row">
        <IonCol className="bhp-online-health-table-col-even" size="7">
          Do you have a personal Website?
        </IonCol>
        <IonCol
          className="bhp-online-health-table-col-even"
          size="2.5"
          onClick={() => {
            setOnlineHealth((prevState) => {
              const retVal: BhpOnlineHealthDataType = deepCopy(prevState);
              retVal.website = !retVal.website;
              return retVal;
            });
          }}
        >
          <div
            className="bhp-online-health-center-align"
            style={{ fontSize: "1.2rem" }}
          >
            <IonIcon
              icon={onlineHealth.website ? checkmarkCircle : ellipse}
              color={onlineHealth.website ? "primary" : "medium"}
            />
          </div>
        </IonCol>
        <IonCol
          className="bhp-online-health-table-col-even bhp-online-health-dont-care"
          size="2.5"
        ></IonCol>
      </IonRow> */}
      {onlineHealthChecklistArray.map((el, idx) => {
        return getOnlineHealthChecklistRow(idx, el.label, el.key, el?.subTitle);
      })}
      {/* <IonRow className="bhp-online-health-table-row">
        <IonCol className="bhp-online-health-table-col-even" size="7">
          Number of customer visits.
        </IonCol>
        <IonCol
          className="bhp-online-health-table-col-even bhp-online-health-dont-care"
          size="2.5"
        ></IonCol>
        <IonCol
          className="bhp-online-health-table-col-even bhp-online-health-dont-care"
          size="2.5"
        ></IonCol>
      </IonRow>
      <IonRow className="bhp-online-health-table-row">
        <IonCol className="bhp-online-health-table-col-odd" size="7">
          Number of customer reviews.
        </IonCol>
        <IonCol
          className="bhp-online-health-table-col-odd bhp-online-health-dont-care"
          size="2.5"
        ></IonCol>
        <IonCol
          className="bhp-online-health-table-col-odd bhp-online-health-dont-care"
          size="2.5"
        ></IonCol>
      </IonRow> */}
    </IonGrid>
  );

  return (
    <IonModal
      isOpen={props.open}
      onDidDismiss={onDismiss}
      breakpoints={[1]}
      initialBreakpoint={1}
    >
      <BhpThemeHeaderWithBack
        title={
          props.mode === "CUSTOMER_EX"
            ? "Customer Experience Evaluator"
            : "Online Health Evaluator"
        }
        onBack={onDismiss}
      />
      <IonContent>
        <div style={{ padding: "0 20px", paddingTop: "10px" }}>
          <ProgressSlider
            label={
              props.mode === "CUSTOMER_EX"
                ? "Customer Experience Evaluator"
                : "Online Health Score"
            }
            score={props.mode === "CUSTOMER_EX" ? customerExScore : healthScore}
          />
        </div>
        <div
          style={{
            textAlign: "center",
            width: "100%",
            paddingTop: "10px",
            color: "var(--ion-color-primary-dark)",
          }}
        >
          <b>Checklist</b>
        </div>
        <div style={{ padding: "5px", paddingTop: 0, fontSize: "0.8rem" }}>
          {props.mode === "ONLINE_HEALTH" && onlineHealthJsx}
          {props.mode === "CUSTOMER_EX" && customerExJsx}
        </div>
        <div style={{ padding: "0 20px", paddingTop: "10px" }}>
          <ProgressSlider
            label={"Website Strength"}
            score={props.websiteStrength}
          />
        </div>
      </IonContent>
      <IonFooter>
        <div style={{ padding: "5px" }}>
          <IonButton
            expand="block"
            color={"secondary"}
            onClick={props.clickNext}
          >
            Dashboard
            <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        </div>
      </IonFooter>
    </IonModal>
  );
};

export default BhpOnlineHealth;
